/**
 * OmegaTriggersPage — `/omega/triggers`.
 *
 * Phase 11 trigger observability + founder control surface.
 *
 * Sections:
 *   1. Trigger Health tile  — aggregate stats from GET /triggers/health
 *   2. "Create Trigger" button → CreateTriggerModal
 *   3. Trigger table          — polls GET /triggers/list every 10 s
 *      - Per-row: type badge, specialist, status badge, failure badge
 *      - Actions: enable/disable toggle (→ FounderSignDialog)
 *                 view spec drawer, fire-now (webhook only)
 *      - Expand row → RecentEventsDrawer (GET /triggers/{id}/recent)
 */

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { cn } from '@/utils/cn';
import { api } from '@/api/client';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { canonicalJsonStringify } from '@/api/mock/sign';
import type {
  CronValidateResponse,
  TriggerHealthStats,
  TriggerKind,
  TriggerSummary,
} from '@/api/types';

// ─── Query keys ───────────────────────────────────────────────────

const TRIGGER_KEYS = {
  list: () => ['triggers', 'list'] as const,
  health: () => ['triggers', 'health'] as const,
  recent: (id: string) => ['triggers', 'recent', id] as const,
};

// ─── Hooks ────────────────────────────────────────────────────────

function useTriggerList() {
  return useQuery({
    queryKey: TRIGGER_KEYS.list(),
    queryFn: () => api.listTriggers(),
    refetchInterval: 10_000,
    staleTime: 8_000,
  });
}

function useTriggerHealth() {
  return useQuery({
    queryKey: TRIGGER_KEYS.health(),
    queryFn: () => api.getTriggerHealth(),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });
}

function useTriggerRecentEvents(id: string | null) {
  return useQuery({
    queryKey: TRIGGER_KEYS.recent(id ?? ''),
    queryFn: () => api.getTriggerRecentEvents(id!),
    enabled: id !== null,
    staleTime: 5_000,
  });
}

// ─── Page ────────────────────────────────────────────────────────

export function OmegaTriggersPage() {
  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  const [createOpen, setCreateOpen] = useState(false);
  const [signTarget, setSignTarget] = useState<SignTarget | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [specDrawerId, setSpecDrawerId] = useState<string | null>(null);
  const [fireResult, setFireResult] = useState<string | null>(null);

  const listQuery = useTriggerList();
  const healthQuery = useTriggerHealth();
  const qc = useQueryClient();

  const fireMutation = useMutation({
    mutationFn: (id: string) => api.fireTrigger(id),
    onSuccess: (data) => {
      setFireResult(`Fired at ${data.fired_at}. ${data.message}`);
      void qc.invalidateQueries({ queryKey: TRIGGER_KEYS.list() });
    },
    onError: (e: Error) => setFireResult(`Error: ${e.message}`),
  });

  const toggleMutation = useMutation({
    mutationFn: (p: { id: string; req: Parameters<typeof api.setTriggerEnabled>[1]; enabled: boolean; sig: string }) =>
      api.setTriggerEnabled(p.id, p.req, p.enabled, p.sig),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TRIGGER_KEYS.list() });
      setSignTarget(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (p: { req: Parameters<typeof api.createTrigger>[0]; sig: string }) =>
      api.createTrigger(p.req, p.sig),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TRIGGER_KEYS.list() });
      setSignTarget(null);
      setCreateOpen(false);
    },
  });

  const triggers = listQuery.data ?? [];
  const health = healthQuery.data;

  const specTrigger =
    specDrawerId !== null
      ? triggers.find((t) => t.trigger_id === specDrawerId) ?? null
      : null;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb label="Triggers" />

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          Triggers
        </h1>
        <p className="text-sm text-ink-muted">
          Registered triggers that fire specialists automatically. Cron,
          webhook, log-event, vault-event, and chain types. All mutations
          require a founder signature.
        </p>
      </header>

      {/* Health tile */}
      {health ? (
        <TriggerHealthTile stats={health} />
      ) : (
        <div className="h-24 animate-pulse rounded-xl border border-border bg-canvas" />
      )}

      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-ink-muted">
          {triggers.length} trigger{triggers.length !== 1 ? 's' : ''} registered
        </span>
        <button
          type="button"
          data-testid="trigger-create-btn"
          onClick={() => setCreateOpen(true)}
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-4 py-2',
            'border border-border bg-surface text-sm font-semibold text-ink',
            'transition-colors hover:border-ink-muted hover:bg-canvas',
          )}
        >
          + Create Trigger
        </button>
      </div>

      {/* Fire result banner */}
      {fireResult && (
        <div className="rounded-md border border-success/30 bg-success/5 px-4 py-3 text-sm text-ink">
          {fireResult}
          <button
            type="button"
            onClick={() => setFireResult(null)}
            className="ms-3 text-ink-muted hover:text-ink"
          >
            ✕
          </button>
        </div>
      )}

      {/* Toggle mutation error */}
      {toggleMutation.isError && (
        <div className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          {toggleMutation.error.message}
        </div>
      )}
      {createMutation.isError && (
        <div className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          {createMutation.error.message}
        </div>
      )}

      {/* Trigger table */}
      <section className="rounded-xl border border-border bg-surface">
        {listQuery.isPending ? (
          <div className="px-6 py-8 text-center text-sm text-ink-subtle">
            Loading triggers…
          </div>
        ) : triggers.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-ink-subtle">
            No triggers registered yet. Use "Create Trigger" to add one.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="triggers-table">
            <thead>
              <tr className="border-b border-border">
                {['ID', 'Type', 'Specialist', 'Status', 'Failures', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-start text-[10px] font-semibold uppercase tracking-wider text-ink-subtle"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {triggers.map((t) => (
                <>
                  <TriggerRow
                    key={t.trigger_id}
                    trigger={t}
                    expanded={expandedId === t.trigger_id}
                    onToggleExpand={() =>
                      setExpandedId((prev) =>
                        prev === t.trigger_id ? null : t.trigger_id,
                      )
                    }
                    onViewSpec={() => setSpecDrawerId(t.trigger_id)}
                    onFire={() => {
                      setFireResult(null);
                      fireMutation.mutate(t.trigger_id);
                    }}
                    onToggleEnabled={() => {
                      const req = {
                        nonce: crypto.randomUUID(),
                        trigger_id: t.trigger_id,
                      };
                      const payload = canonicalJsonStringify(req);
                      setSignTarget({
                        kind: 'toggle',
                        label: t.enabled ? 'Disable trigger' : 'Enable trigger',
                        payload,
                        onSign: (sig) =>
                          toggleMutation.mutate({
                            id: t.trigger_id,
                            req,
                            enabled: !t.enabled,
                            sig,
                          }),
                      });
                    }}
                    isPending={fireMutation.isPending && fireMutation.variables === t.trigger_id}
                  />
                  {expandedId === t.trigger_id && (
                    <tr key={`${t.trigger_id}-expanded`}>
                      <td colSpan={6} className="bg-canvas px-4 pb-4">
                        <RecentEventsPanel triggerId={t.trigger_id} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Modals & drawers */}
      {createOpen && (
        <CreateTriggerModal
          specialists={[...new Set(triggers.map((t) => t.specialist_id))]}
          onClose={() => setCreateOpen(false)}
          onSubmit={(req) => {
            const payload = canonicalJsonStringify(req);
            setSignTarget({
              kind: 'create',
              label: 'Create trigger',
              payload,
              onSign: (sig) => createMutation.mutate({ req, sig }),
            });
          }}
        />
      )}

      {signTarget && (() => {
        const sigError = toggleMutation.isError
          ? toggleMutation.error.message
          : createMutation.isError
            ? createMutation.error.message
            : undefined;
        return (
          <FounderSignDialog
            label={signTarget.label}
            payload={signTarget.payload}
            isPending={toggleMutation.isPending || createMutation.isPending}
            {...(sigError ? { error: sigError } : {})}
            onSign={signTarget.onSign}
            onClose={() => setSignTarget(null)}
          />
        );
      })()}

      {specTrigger && (
        <TriggerSpecDrawer
          trigger={specTrigger}
          onClose={() => setSpecDrawerId(null)}
        />
      )}
    </div>
  );
}

// ─── TriggerHealthTile ────────────────────────────────────────────

function TriggerHealthTile({ stats }: { stats: TriggerHealthStats }) {
  const trafficLight =
    stats.auto_disabled_count > 0
      ? 'red'
      : stats.currently_failing > 0
        ? 'yellow'
        : 'green';

  return (
    <section
      aria-label="Trigger health"
      className={cn(
        'rounded-xl border-2 bg-surface p-5',
        trafficLight === 'green' && 'border-success/30',
        trafficLight === 'yellow' && 'border-warning/30',
        trafficLight === 'red' && 'border-danger/30',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">
          Trigger Health
        </h2>
        <TrafficLightBadge state={trafficLight} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatCard label="Enabled" value={stats.total_enabled} />
        <StatCard label="Disabled" value={stats.total_disabled} />
        <StatCard
          label="Failing"
          value={stats.currently_failing}
          {...(stats.currently_failing > 0 ? { color: 'yellow' as const } : {})}
        />
        <StatCard
          label="Auto-disabled"
          value={stats.auto_disabled_count}
          {...(stats.auto_disabled_count > 0 ? { color: 'red' as const } : {})}
        />
        <StatCard
          label="Overdue cron"
          value={stats.overdue_cron_count}
          {...(stats.overdue_cron_count > 0 ? { color: 'yellow' as const } : {})}
        />
      </div>
    </section>
  );
}

function TrafficLightBadge({ state }: { state: 'green' | 'yellow' | 'red' }) {
  const label = state === 'green' ? 'Healthy' : state === 'yellow' ? 'Degraded' : 'Critical';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1',
        'text-xs font-semibold uppercase tracking-wide',
        state === 'green' && 'border-success/30 bg-success-soft text-success',
        state === 'yellow' && 'border-warning/40 bg-warning/10 text-warning',
        state === 'red' && 'border-danger/40 bg-danger/10 text-danger',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          state === 'green' && 'bg-success',
          state === 'yellow' && 'bg-warning',
          state === 'red' && 'bg-danger',
        )}
      />
      {label}
    </span>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: 'yellow' | 'red';
}) {
  return (
    <div className="rounded-lg border border-border bg-canvas px-3 py-3">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </div>
      <div
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          !color && 'text-ink',
          color === 'yellow' && 'text-warning',
          color === 'red' && 'text-danger',
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ─── TriggerRow ───────────────────────────────────────────────────

function TriggerRow({
  trigger,
  expanded,
  onToggleExpand,
  onViewSpec,
  onFire,
  onToggleEnabled,
  isPending,
}: {
  trigger: TriggerSummary;
  expanded: boolean;
  onToggleExpand: () => void;
  onViewSpec: () => void;
  onFire: () => void;
  onToggleEnabled: () => void;
  isPending: boolean;
}) {
  const isWebhook = trigger.trigger_type.kind === 'webhook';
  const shortId = trigger.trigger_id.slice(0, 8);

  return (
    <tr
      className={cn(
        'border-b border-border transition-colors hover:bg-canvas',
        expanded && 'bg-canvas',
      )}
    >
      {/* ID */}
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={onToggleExpand}
          title="Toggle recent events"
          className="flex items-center gap-1.5 font-mono text-xs text-ink-muted hover:text-ink"
        >
          <span aria-hidden className="text-[10px]">
            {expanded ? '▼' : '▶'}
          </span>
          {shortId}…
        </button>
      </td>

      {/* Type */}
      <td className="px-4 py-3">
        <TriggerTypeBadge kind={trigger.trigger_type} />
      </td>

      {/* Specialist */}
      <td className="px-4 py-3 font-mono text-xs text-ink">
        {trigger.specialist_id}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge enabled={trigger.enabled} />
      </td>

      {/* Failures */}
      <td className="px-4 py-3">
        <FailuresBadge count={trigger.consecutive_failures} />
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`trigger-toggle-${trigger.trigger_id}`}
            onClick={onToggleEnabled}
            className={cn(
              'rounded px-2 py-1 text-xs font-semibold transition-colors',
              trigger.enabled
                ? 'border border-warning/40 text-warning hover:bg-warning/10'
                : 'border border-success/30 text-success hover:bg-success/10',
            )}
          >
            {trigger.enabled ? 'Disable' : 'Enable'}
          </button>

          <button
            type="button"
            onClick={onViewSpec}
            className="rounded border border-border px-2 py-1 text-xs text-ink-muted hover:bg-canvas hover:text-ink"
          >
            View
          </button>

          {isWebhook && (
            <button
              type="button"
              data-testid={`trigger-fire-${trigger.trigger_id}`}
              disabled={!trigger.enabled || isPending}
              onClick={onFire}
              className={cn(
                'rounded border px-2 py-1 text-xs font-semibold transition-colors',
                trigger.enabled
                  ? 'border-border text-ink hover:bg-canvas'
                  : 'cursor-not-allowed border-border opacity-40 text-ink-subtle',
              )}
            >
              {isPending ? '…' : 'Fire'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Badges ───────────────────────────────────────────────────────

function TriggerTypeBadge({ kind }: { kind: TriggerKind }) {
  const { label, colorClass } = typeMeta(kind);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5',
        'text-[10px] font-semibold uppercase tracking-wide',
        colorClass,
      )}
    >
      {label}
    </span>
  );
}

function typeMeta(kind: TriggerKind): { label: string; colorClass: string } {
  switch (kind.kind) {
    case 'cron':
      return {
        label: 'cron',
        colorClass: 'border-blue-500/40 bg-blue-500/10 text-blue-400',
      };
    case 'webhook':
      return {
        label: 'webhook',
        colorClass: 'border-purple-500/40 bg-purple-500/10 text-purple-400',
      };
    case 'log_event':
      return {
        label: 'log-event',
        colorClass: 'border-amber-500/40 bg-amber-500/10 text-amber-400',
      };
    case 'vault_event':
      return {
        label: 'vault-event',
        colorClass: 'border-teal-500/40 bg-teal-500/10 text-teal-400',
      };
    case 'chain':
      return {
        label: 'chain',
        colorClass: 'border-pink-500/40 bg-pink-500/10 text-pink-400',
      };
  }
}

function StatusBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      data-testid={enabled ? 'status-enabled' : 'status-disabled'}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5',
        'text-[10px] font-semibold uppercase tracking-wide',
        enabled
          ? 'border-success/30 bg-success-soft text-success'
          : 'border-border bg-canvas text-ink-subtle',
      )}
    >
      <span
        aria-hidden
        className={cn('h-1.5 w-1.5 rounded-full', enabled ? 'bg-success' : 'bg-ink-subtle')}
      />
      {enabled ? 'enabled' : 'disabled'}
    </span>
  );
}

function FailuresBadge({ count }: { count: number }) {
  const colorClass =
    count === 0
      ? 'text-success border-success/30 bg-success-soft'
      : count <= 2
        ? 'text-warning border-warning/40 bg-warning/10'
        : count <= 4
          ? 'text-orange-400 border-orange-400/40 bg-orange-400/10'
          : 'text-danger border-danger/40 bg-danger/10';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5',
        'font-mono text-[10px] font-semibold',
        colorClass,
      )}
    >
      {count}
    </span>
  );
}

// ─── RecentEventsPanel ────────────────────────────────────────────

function RecentEventsPanel({ triggerId }: { triggerId: string }) {
  const { data, isPending } = useTriggerRecentEvents(triggerId);

  if (isPending) {
    return (
      <p className="py-4 text-xs text-ink-subtle">Loading recent events…</p>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-xs text-ink-subtle">
        No fire events recorded yet.
      </p>
    );
  }

  return (
    <div data-testid="recent-events-panel">
      <h3 className="mb-2 text-[10px] uppercase tracking-wider text-ink-subtle">
        Recent fire events (last {data.length})
      </h3>
      <ol className="flex flex-col gap-1.5">
        {data.map((ev) => (
          <li
            key={ev.sequence}
            className="flex items-center gap-3 rounded border border-border bg-surface px-3 py-2"
          >
            <span className="font-mono text-[10px] text-ink-subtle">
              #{ev.sequence}
            </span>
            <span className="text-xs text-ink">
              {formatRelative(ev.fired_at)}
            </span>
            <span className="font-mono text-[10px] text-ink-muted">
              {ev.source}
            </span>
            <span className="ms-auto font-mono text-[10px] text-ink-subtle">
              {ev.specialist_id}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─── TriggerSpecDrawer ────────────────────────────────────────────

function TriggerSpecDrawer({
  trigger,
  onClose,
}: {
  trigger: TriggerSummary;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-end"
      role="dialog"
      aria-label="Trigger spec"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <aside className="relative z-10 flex h-full w-full max-w-md flex-col gap-4 overflow-y-auto border-l border-border bg-surface p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">Trigger spec</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink"
          >
            ✕
          </button>
        </div>

        <dl className="flex flex-col gap-3 text-sm">
          <DrawerField label="ID" value={trigger.trigger_id} mono />
          <DrawerField label="Specialist" value={trigger.specialist_id} mono />
          <DrawerField label="Kind" value={trigger.trigger_type.kind} />
          {trigger.trigger_type.kind === 'cron' && (
            <DrawerField
              label="Expression"
              value={trigger.trigger_type.expression}
              mono
            />
          )}
          {trigger.trigger_type.kind === 'log_event' && (
            <DrawerField
              label="Entry type filter"
              value={trigger.trigger_type.entry_type_filter}
              mono
            />
          )}
          {trigger.trigger_type.kind === 'vault_event' && (
            <DrawerField
              label="Event kind"
              value={trigger.trigger_type.event_kind}
              mono
            />
          )}
          {trigger.trigger_type.kind === 'chain' && (
            <DrawerField
              label="After specialist"
              value={trigger.trigger_type.after_specialist_id}
              mono
            />
          )}
          <DrawerField
            label="Status"
            value={trigger.enabled ? 'enabled' : 'disabled'}
          />
          <DrawerField
            label="Consecutive failures"
            value={String(trigger.consecutive_failures)}
          />
        </dl>
      </aside>
    </div>
  );
}

function DrawerField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-canvas px-3 py-3">
      <dt className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </dt>
      <dd
        className={cn(
          'break-all text-sm text-ink',
          mono && 'font-mono text-xs',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

// ─── FounderSignDialog ────────────────────────────────────────────

interface SignTarget {
  kind: 'toggle' | 'create';
  label: string;
  payload: string;
  onSign: (sig: string) => void;
}

function FounderSignDialog({
  label,
  payload,
  isPending,
  error,
  onSign,
  onClose,
}: {
  label: string;
  payload: string;
  isPending: boolean;
  error?: string;
  onSign: (sig: string) => void;
  onClose: () => void;
}) {
  const [sig, setSig] = useState('');
  const [autoSignPending, setAutoSignPending] = useState(false);
  const [autoSignError, setAutoSignError] = useState('');

  // Compute the canonical hash of the JSON payload for the founder to sign.
  // The server runs: canonical_hash(req) → sha256:<hex>, then verifies the
  // signature over those bytes. Match that here.
  const payloadBytes = new TextEncoder().encode(payload);
  const hashHex = bytesToHex(sha256(payloadBytes));
  const hashString = `sha256:${hashHex}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Founder sign dialog"
      data-testid="founder-sign-dialog"
    >
      <div className="flex w-full max-w-lg flex-col gap-5 rounded-xl border border-border bg-surface p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">{label}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink"
          >
            ✕
          </button>
        </div>

        <p className="text-sm text-ink-muted">
          Sign the hash below with your founder Ed25519 key. Paste the result
          in the field at the bottom.
        </p>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Canonical payload (JCS JSON)
          </label>
          <pre className="max-h-32 overflow-auto rounded-md border border-border bg-canvas px-3 py-2 font-mono text-[11px] text-ink-muted">
            {payload}
          </pre>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Hash to sign
          </label>
          <div
            data-testid="hash-to-sign"
            className="flex items-center gap-2 rounded-md border border-border bg-canvas px-3 py-2"
          >
            <code className="flex-1 break-all font-mono text-[11px] text-ink">
              {hashString}
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(hashString)}
              className="shrink-0 rounded border border-border px-2 py-1 text-[10px] text-ink-muted hover:text-ink"
            >
              Copy
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label htmlFor="founder-sig-input" className="text-[10px] uppercase tracking-wider text-ink-subtle">
              Founder signature
            </label>
            <button
              type="button"
              disabled={autoSignPending}
              onClick={() => {
                setAutoSignPending(true);
                setAutoSignError('');
                signHashWithFounderKey(hashHex)
                  .then((sig) => { setSig(sig); setAutoSignPending(false); })
                  .catch((e) => { setAutoSignError(e instanceof Error ? e.message : 'Sign failed'); setAutoSignPending(false); });
              }}
              className="text-[10px] text-spark hover:underline disabled:opacity-50"
            >
              {autoSignPending ? 'Signing…' : 'Sign with my key ↗'}
            </button>
          </div>
          {autoSignError && <p className="text-[10px] text-danger">{autoSignError}</p>}
          <input
            id="founder-sig-input"
            data-testid="founder-sig-input"
            type="text"
            placeholder="ed25519:BASE64_SIGNATURE"
            value={sig}
            onChange={(e) => setSig(e.target.value)}
            className={cn(
              'rounded-md border border-border bg-canvas px-3 py-2',
              'font-mono text-xs text-ink placeholder:text-ink-subtle',
              'focus:border-ink-muted focus:outline-none',
            )}
          />
          {error && (
            <p
              data-testid="founder-sig-error"
              className="text-xs text-danger"
            >
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm text-ink-muted hover:bg-canvas"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="founder-sign-submit"
            disabled={!sig.trim() || isPending}
            onClick={() => onSign(sig.trim())}
            className={cn(
              'rounded-md border border-ink/30 bg-canvas px-4 py-2',
              'text-sm font-semibold text-ink transition-colors',
              'hover:border-ink hover:bg-surface',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            {isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CreateTriggerModal ───────────────────────────────────────────

function CreateTriggerModal({
  specialists,
  onClose,
  onSubmit,
}: {
  specialists: string[];
  onClose: () => void;
  onSubmit: (req: Parameters<typeof api.createTrigger>[0]) => void;
}) {
  const [kind, setKind] = useState<TriggerKind['kind']>('webhook');
  const [specialistId, setSpecialistId] = useState(specialists[0] ?? '');
  const [expression, setExpression] = useState('0 * * * *');
  const [entryTypeFilter, setEntryTypeFilter] = useState('entity');
  const [eventKind, setEventKind] = useState('created');
  const [afterSpecialist, setAfterSpecialist] = useState('');
  const [inputTemplate, setInputTemplate] = useState('{}');
  const [templateError, setTemplateError] = useState('');
  const [cronValidation, setCronValidation] = useState<CronValidateResponse | null>(null);
  const [cronPending, setCronPending] = useState(false);

  async function checkCron(expr: string) {
    if (!expr.trim()) return;
    setCronPending(true);
    try {
      const result = await api.validateCronExpression(expr);
      setCronValidation(result);
    } catch {
      setCronValidation({ valid: false, next_fire_times: [], error: 'Validation failed' });
    } finally {
      setCronPending(false);
    }
  }

  function buildTriggerType(): TriggerKind {
    switch (kind) {
      case 'cron':
        return { kind: 'cron', expression };
      case 'webhook':
        return { kind: 'webhook' };
      case 'log_event':
        return { kind: 'log_event', entry_type_filter: entryTypeFilter };
      case 'vault_event':
        return { kind: 'vault_event', event_kind: eventKind };
      case 'chain':
        return { kind: 'chain', after_specialist_id: afterSpecialist };
    }
  }

  function handleSubmit() {
    let template: unknown = {};
    try {
      template = JSON.parse(inputTemplate);
      setTemplateError('');
    } catch {
      setTemplateError('Invalid JSON in input template');
      return;
    }
    onSubmit({
      input_template: template,
      nonce: crypto.randomUUID(),
      specialist_id: specialistId,
      trigger_type: buildTriggerType(),
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Create trigger"
      data-testid="create-trigger-modal"
    >
      <div className="flex w-full max-w-lg flex-col gap-5 rounded-xl border border-border bg-surface p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">Create Trigger</h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>

        <div className="flex flex-col gap-4 text-sm">
          {/* Trigger type selector */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
              Type
            </label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as TriggerKind['kind'])}
              className="rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink focus:outline-none"
            >
              <option value="cron">Cron</option>
              <option value="webhook">Webhook</option>
              <option value="log_event">Log Event</option>
              <option value="vault_event">Vault Event</option>
              <option value="chain">Chain</option>
            </select>
          </div>

          {/* Specialist selector */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
              Specialist ID
            </label>
            {specialists.length > 0 ? (
              <select
                value={specialistId}
                onChange={(e) => setSpecialistId(e.target.value)}
                className="rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink focus:outline-none"
              >
                {specialists.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder="specialist-id"
                value={specialistId}
                onChange={(e) => setSpecialistId(e.target.value)}
                className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-xs text-ink focus:outline-none"
              />
            )}
          </div>

          {/* Cron expression + validator */}
          {kind === 'cron' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
                Cron expression
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={expression}
                  onChange={(e) => {
                    setExpression(e.target.value);
                    setCronValidation(null);
                  }}
                  className="flex-1 rounded-md border border-border bg-canvas px-3 py-2 font-mono text-xs text-ink focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => void checkCron(expression)}
                  disabled={cronPending}
                  className="rounded-md border border-border px-3 py-2 text-xs text-ink-muted hover:bg-canvas"
                >
                  {cronPending ? '…' : 'Validate'}
                </button>
              </div>
              {cronValidation && (
                <div
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs',
                    cronValidation.valid
                      ? 'border-success/30 bg-success/5 text-success'
                      : 'border-danger/30 bg-danger/5 text-danger',
                  )}
                  data-testid="cron-validation-result"
                >
                  {cronValidation.valid ? (
                    <>
                      Valid. Next fires:{' '}
                      {cronValidation.next_fire_times.slice(0, 3).join(', ')}
                    </>
                  ) : (
                    cronValidation.error ?? 'Invalid expression'
                  )}
                </div>
              )}
            </div>
          )}

          {kind === 'log_event' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
                Entry type filter
              </label>
              <input
                type="text"
                value={entryTypeFilter}
                onChange={(e) => setEntryTypeFilter(e.target.value)}
                placeholder="entity"
                className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-xs text-ink focus:outline-none"
              />
            </div>
          )}

          {kind === 'vault_event' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
                Event kind
              </label>
              <input
                type="text"
                value={eventKind}
                onChange={(e) => setEventKind(e.target.value)}
                placeholder="created"
                className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-xs text-ink focus:outline-none"
              />
            </div>
          )}

          {kind === 'chain' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
                After specialist ID
              </label>
              <input
                type="text"
                value={afterSpecialist}
                onChange={(e) => setAfterSpecialist(e.target.value)}
                placeholder="specialist-id"
                className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-xs text-ink focus:outline-none"
              />
            </div>
          )}

          {/* Input template */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
              Input template (JSON)
            </label>
            <textarea
              rows={3}
              value={inputTemplate}
              onChange={(e) => {
                setInputTemplate(e.target.value);
                setTemplateError('');
              }}
              className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-xs text-ink focus:outline-none"
            />
            {templateError && (
              <p className="text-xs text-danger">{templateError}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm text-ink-muted hover:bg-canvas"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="create-trigger-submit"
            disabled={!specialistId.trim()}
            onClick={handleSubmit}
            className={cn(
              'rounded-md border border-ink/30 bg-canvas px-4 py-2',
              'text-sm font-semibold text-ink',
              'hover:border-ink hover:bg-surface',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            Continue to sign
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

function Breadcrumb({ label }: { label: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
      <Link
        to="/omega"
        className="text-ink-muted transition-colors hover:text-ink"
      >
        &larr; OMEGA
      </Link>
      <span className="text-ink-subtle">/</span>
      <span className="text-ink">{label}</span>
    </nav>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}
